import { FunnelIcon } from '@heroicons/react/16/solid';
import { DynamicForm, IDynamicFormRef, Modal } from '@src/components';
import { userFilterSchema } from '@src/formSchemas/user/userFilterSchema';
import { formatDate } from '@src/utilities/dateUtils';
import {
    applyFilteredFormData,
    areFiltersDirty,
    FilterFormValue,
} from '@src/utilities/form';
import { Button } from 'antd';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

type UsersFilterProps = {
    initialFormData?: Record<string, any>;
    onChange?: (key: string, value: FilterFormValue) => void;
    onClear?: () => void;
    onClose?: () => void;
};

export function UsersFilter({
    initialFormData,
    onChange,
    onClear,
    onClose,
}: Readonly<UsersFilterProps>) {
    const { t } = useTranslation();
    const formRef = useRef<IDynamicFormRef>(null);
    const [isDirty, setIsDirty] = useState(false);
    const [formKey, setFormKey] = useState(0);
    const [account, setAccount] = useState<
        { label: string; value: string } | undefined
    >(() => {
        const accountValue = initialFormData?.account;
        if (
            accountValue &&
            typeof accountValue === 'object' &&
            accountValue.value
        ) {
            return accountValue;
        }
        return undefined;
    });

    useEffect(() => {
        const accountValue = initialFormData?.account;
        if (
            accountValue &&
            typeof accountValue === 'object' &&
            accountValue.value
        ) {
            setAccount(accountValue);
        } else if (!accountValue) {
            setAccount(undefined);
        }
    }, [initialFormData]);

    const handleSubmit = useCallback(
        (formData: FieldValues) => {
            if (onChange) {
                const normalizedData = { ...formData };

                for (const key in normalizedData) {
                    const value = normalizedData[key];

                    if (dayjs.isDayjs(value)) {
                        normalizedData[key] = formatDate(
                            value.toString(),
                            'dateOnly'
                        );
                    }
                }
                applyFilteredFormData(normalizedData, onChange);
            }
            onClose?.();
        },
        [onChange, onClose]
    );

    const handleFooterSubmit = async () => {
        if (!formRef.current) return;

        const isValid = await formRef.current.trigger();
        if (!isValid) return;

        const values = formRef.current.getValues();
        handleSubmit(values);
    };

    function normalizeInitialFormData(
        formData?: Record<string, any>
    ): Record<string, any> | undefined {
        if (!formData) return undefined;

        const normalizedData: Record<string, any> = {};

        for (const key in formData) {
            const value = formData[key];

            if (
                typeof value === 'string' &&
                dayjs(value, 'YYYY-MM-DD', true).isValid()
            ) {
                normalizedData[key] = dayjs(value);
            } else {
                normalizedData[key] = value;
            }
        }

        return normalizedData;
    }
    const normalizedInitialFormData = useMemo(
        () => normalizeInitialFormData(initialFormData),
        [initialFormData]
    );

    const handleAccountChange = (
        account: { label: string; value: string } | null | undefined
    ) => {
        setAccount(account || undefined);
        if (!account && formRef.current) {
            formRef.current.setValue('role', null);
        }
    };

    const handleClearAll = () => {
        formRef.current?.reset();
        setAccount(undefined);
        onClear?.();
        setFormKey((prev) => prev + 1);
    };

    return (
        <Modal
            title={t('pages.users.forms.filter.title')}
            headerIcon={<FunnelIcon width={20} height={20} />}
            isOpen={true}
            onClose={() => onClose && onClose()}
            content={
                <DynamicForm
                    key={formKey}
                    ref={formRef}
                    formData={normalizedInitialFormData}
                    resetFormData
                    isLoading={false}
                    formGroups={userFilterSchema({
                        t,
                        accountId: account ? account.value : '',
                        onAccountChange: handleAccountChange,
                    })}
                    formKeyName="user-filter-form"
                    onHandleSubmit={handleSubmit}
                    hasDataChanged={() => {
                        const values = formRef.current?.getValues() ?? {};
                        setIsDirty(areFiltersDirty(values, initialFormData));
                    }}
                />
            }
            footer={
                <div className="w-full flex justify-between text-center space-x-2">
                    <Button
                        size="large"
                        className="btn-secondary w-full justify-center"
                        variant="outlined"
                        onClick={handleClearAll}
                    >
                        {t('buttons.clearAll')}
                    </Button>
                    <Button
                        size="large"
                        htmlType="submit"
                        className="btn-primary w-full text-center justify-center"
                        onClick={handleFooterSubmit}
                        disabled={!isDirty}
                    >
                        {t('buttons.apply')}
                    </Button>
                </div>
            }
        />
    );
}
